"use client";

import { motion } from "framer-motion";
import { AlertTriangle, ArrowRight, CheckCircle, TrendingUp, DollarSign, Clock, Users } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import { Button } from "@/components/ui/button";

const problems = [
  {
    icon: <Clock className="w-6 h-6" />,
    title: "Your Team is Stuck in Manual Hell",
    description: "Data entry, report generation, customer follow-ups—your skilled employees are doing work a computer should handle",
    impact: "$75K+ wasted annually per employee",
    example: "8 hours/day on manual tasks"
  },
  {
    icon: <AlertTriangle className="w-6 h-6" />,
    title: "Scaling Means Hiring More People",
    description: "Every new client requires more staff. Your growth is limited by how fast you can recruit and train",
    impact: "Growth ceiling at 2-3x",
    example: "Can't handle 10x more orders"
  },
  {
    icon: <DollarSign className="w-6 h-6" />,
    title: "Human Errors Cost Real Money",
    description: "Missed deadlines, data mistakes, inconsistent processes—manual work creates expensive problems",
    impact: "5-15% revenue loss",
    example: "One mistake = $10K+ loss"
  }
];

const solutions = [
  {
    icon: <CheckCircle className="w-6 h-6" />,
    title: "AI Agents Work 24/7",
    description: "Intelligent automation handles data processing, customer communications, and reporting while you sleep",
    impact: "80% time savings",
    example: "8 hours → 30 minutes"
  },
  {
    icon: <Users className="w-6 h-6" />,
    title: "Scale Without Hiring",
    description: "Handle 10x more volume with your existing team. AI agents don't need training, breaks, or benefits",
    impact: "Unlimited scalability",
    example: "Same team, 10x capacity"
  },
  {
    icon: <TrendingUp className="w-6 h-6" />,
    title: "99.9% Accuracy Guaranteed",
    description: "Eliminate human error with AI systems that follow processes perfectly every single time",
    impact: "Zero costly mistakes",
    example: "Perfect execution, always"
  }
];

const ProblemSolution = () => {
  return (
    <section className="py-20 px-6">
      <div className="container max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <SectionHeading title="Why Smart Businesses Choose AI Over More Employees" className="text-center" />
          <p className="text-lg text-muted-foreground mt-4 max-w-3xl mx-auto">
            The math is simple: AI agents cost 90% less than employees and work 3x faster with zero errors
          </p>
        </div>

        {/* Problem Section */}
        <div className="mb-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, margin: "-100px" }}
            className="text-center mb-12"
          >
            <h3 className="text-2xl md:text-3xl font-bold mb-4 text-destructive">The Hidden Cost of Manual Work</h3>
            <p className="text-xl text-muted-foreground">
              Every hour your team spends on repetitive tasks is money walking out the door
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {problems.map((problem, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true, margin: "-100px" }}
                className="bg-destructive/5 border border-destructive/20 rounded-2xl p-6"
              >
                <div className="bg-destructive/10 p-4 rounded-full w-fit mb-4">
                  <div className="text-destructive">
                    {problem.icon}
                  </div>
                </div>
                <h4 className="text-xl font-bold mb-3">{problem.title}</h4>
                <p className="text-muted-foreground mb-4">{problem.description}</p>
                <div className="space-y-2">
                  <div className="text-sm font-semibold text-destructive bg-destructive/10 px-3 py-1 rounded-full inline-block">
                    {problem.impact}
                  </div>
                  <div className="text-xs text-muted-foreground italic">
                    Example: {problem.example}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Transformation Arrow */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true, margin: "-100px" }}
          className="text-center mb-20"
        >
          <div className="bg-gradient-to-r from-primary to-accent p-8 rounded-2xl max-w-2xl mx-auto">
            <ArrowRight className="w-12 h-12 mx-auto mb-4 text-white" />
            <h3 className="text-2xl font-bold text-white mb-2">The AI Transformation</h3>
            <p className="text-white/90">
              What if your business could run itself while your team focuses on growth and innovation?
            </p>
          </div>
        </motion.div>

        {/* Solution Section */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, margin: "-100px" }}
            className="text-center mb-12"
          >
            <h3 className="text-2xl md:text-3xl font-bold mb-4 text-primary">The AI Advantage</h3>
            <p className="text-xl text-muted-foreground">
              Intelligent automation that works like having a perfect employee who never sleeps, never makes mistakes, and costs 90% less
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {solutions.map((solution, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true, margin: "-100px" }}
                className="bg-primary/5 border border-primary/20 rounded-2xl p-6"
              >
                <div className="bg-primary/10 p-4 rounded-full w-fit mb-4">
                  <div className="text-primary">
                    {solution.icon}
                  </div>
                </div>
                <h4 className="text-xl font-bold mb-3">{solution.title}</h4>
                <p className="text-muted-foreground mb-4">{solution.description}</p>
                <div className="space-y-2">
                  <div className="text-sm font-semibold text-primary bg-primary/10 px-3 py-1 rounded-full inline-block">
                    {solution.impact}
                  </div>
                  <div className="text-xs text-muted-foreground italic">
                    Result: {solution.example}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true, margin: "-100px" }}
            className="text-center"
          >
            <div className="bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 rounded-2xl p-8 max-w-4xl mx-auto">
              <TrendingUp className="w-12 h-12 mx-auto mb-4 text-primary" />
              <h3 className="text-2xl md:text-3xl font-bold mb-4">
                Real Results: $2M+ Saved for Clients
              </h3>
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                My clients typically see 6.5x ROI within 6 months. One e-commerce client went from processing 
                100 orders/day to 1,000 orders/day with the same team size. Another saved $180K annually 
                by automating their customer service workflow.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
                <Button 
                  size="lg" 
                  className="group text-base px-8 rounded-full"
                  onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Get Your Free ROI Calculator
                  <ArrowRight size={18} className="ml-2 transition-transform duration-300 group-hover:translate-x-1" />
                </Button>
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="text-base px-6 rounded-full"
                  asChild
                >
                  <a href="/about">See Case Studies</a>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ProblemSolution; 